import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import { soloModeAccent, soloModeLabel, soloModeUsesClassicProgression } from "../../game/soloModes";
import type { Rect, UiRects } from "../../game/input";
import type { TileData } from "../../game/types";
import type { RenderModel } from "../renderModel";
import type { VersusFeedbackMode } from "../../game/versusFeedbackPolicy";
import type { RankedTier } from "../../versus/ranked";
import type { GameplayCueStackLayout } from "../renderConstants";
import {
  GAME_FONT,
  GAMEPLAY_CUE_STACK_CASH_FONT_PX,
  GAMEPLAY_CUE_STACK_CASH_MAX_SCALE,
  GAMEPLAY_CUE_STACK_HYPE_FONT_PX,
  GAMEPLAY_CUE_STACK_HYPE_MAX_SCALE,
  GAMEPLAY_OBJECTIVE_REWARD_GRANT_GAP,
  GAMEPLAY_OBJECTIVE_REWARD_ICON_SIZE,
  GAMEPLAY_OBJECTIVE_REWARD_ICON_TEXT_GAP,
  GAMEPLAY_OBJECTIVE_SIDE_PAD,
  GAMEPLAY_TOP_STATS_DESKTOP_FONT_PX,
  GAMEPLAY_TOP_STATS_FALLBACK_FONT_PX,
  GAMEPLAY_TOP_STATS_MOBILE_FONT_PX,
  LEVEL_BADGE_REDUCED_MOTION_SCALE,
  LEVEL_BADGE_WIGGLE_DURATION_MS,
  UI_EDGE_PAD,
  compactCount,
  drawGearButton,
  isHeaderGemHotspotEnabledForPhase,
  mFont,
  pxFont,
  prestigeStarSuffix,
  resolveColorHuntGoalPreviewLayout,
  resolveDesktopMenuHeaderNameRowLayout,
  resolveGameplayCueStackLayout,
  resolveGameplayObjectiveBlockLayout,
  resolveGameplayObjectiveLowerLineWidth,
  resolveGameplayObjectiveNumberBoxWidth,
  resolveGameplayObjectiveRowHeight,
  resolveGameplayObjectiveSubheadingFontPx,
  resolveGameplayObjectiveSubtextFontPx,
  resolveGameplayTopRightHudControl,
  resolveGameplayTopRightHudRect,
  resolveGameplayTopStatLayout,
  resolveGameplayTopStatShadowStyle,
  resolveLevelBadgeTheme,
  resolveLevelBadgeWiggle,
  resolveMenuHeaderTopRightActionRects,
  resolveMenuSafeTopOffset,
  resolveMobileMenuHeaderNameRowLayout,
  resolveOnboardingRafsbyLayout,
  resolvePortraitContainRect,
  resolveTitleHeaderResourceRowLayout,
  resolveTitleLogoScale,
  roundedRectPath,
} from "../renderConstants";
import { clamp01 } from "../../shared/easing";

// ── Mutable state refs ─────────────────────────────────────────────

export interface HeaderMutableState {
  coinBounceMs: number;
  gemBounceMs: number;
  headerCoinIconRect: Rect | null;
  headerGemIconRect: Rect | null;
  lastSeenGameplayLevel: number | null;
  levelBadgeWiggleStartMs: number;
}

// ── Dependency interface ────────────────────────────────────────────

export interface HeaderDeps {
  ctx: CanvasRenderingContext2D;
  state: HeaderMutableState;
  reducedMotionPreferred: boolean;

  // Time
  nowMs: () => number;
  nowSec: () => number;

  // Text measurement
  measureTextWidth: (text: string) => number;
  measureTextWidthAtPx: (text: string, fontPx: number, family?: string) => number;

  // Drawing helpers
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number) => void;
  loadCharacterImage: (path: string) => void;
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  drawHudStarIcon: (x: number, y: number, size: number) => void;
  drawHudCashIcon: (x: number, y: number, size: number) => void;
  drawTileSprite: (tile: TileData, x: number, y: number, size: number) => void;
  drawCirclePauseButton: (rect: Rect) => void;
  drawComboBadge: (count: number, fill: number) => void;
  drawGameplayCueStack: (model: RenderModel, layout: GameplayCueStackLayout | null, mode: VersusFeedbackMode) => void;
  valueBounceScale: (bounceMs: number) => number;
  getFrameLinearGradient: (key: string, factory: () => CanvasGradient) => CanvasGradient;

  // Pre-loaded images
  uiSettingsIcon: HTMLImageElement | null;
  settingsIconImage: HTMLImageElement | null;
  uiContactIcon: HTMLImageElement | null;
  playerIconImage: HTMLImageElement | null;
  rankVinylImages: Partial<Record<RankedTier, HTMLImageElement>>;
  heartLifeImage: HTMLImageElement | null;
  heartLifePremiumImage: HTMLImageElement | null;
  titleLogo: HTMLImageElement | null;
  titleLogoReady: boolean;
  checkCompleteImage: HTMLImageElement | null;
  characterImages: Map<string, HTMLImageElement>;
  menuSafeAreaTop: number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawHeaderScreen(
  deps: HeaderDeps,
  model: RenderModel,
  feedbackMode: VersusFeedbackMode,
): Partial<UiRects> {
  const {
    ctx,
    state,
    reducedMotionPreferred,
    nowMs: getNowMs,
    nowSec: getNowSec,
    measureTextWidth,
    measureTextWidthAtPx,
    drawCharacterPortrait,
    loadCharacterImage,
    drawCoinIcon,
    drawGemIcon,
    drawHudStarIcon,
    drawHudCashIcon,
    drawTileSprite,
    drawCirclePauseButton,
    drawComboBadge,
    drawGameplayCueStack,
    valueBounceScale,
    getFrameLinearGradient,
    uiSettingsIcon,
    settingsIconImage,
    uiContactIcon,
    playerIconImage,
    rankVinylImages,
    heartLifeImage,
    heartLifePremiumImage,
    titleLogo,
    titleLogoReady,
    checkCompleteImage,
    characterImages,
    menuSafeAreaTop,
  } = deps;

  const headerRects: Partial<UiRects> = {};
  const menuPhase = model.phase === "TITLE";
  const gameplayPhase =
    model.phase === "RUN_PLAYABLE" ||
    model.phase === "RUN_RESOLVING" ||
    model.phase === "RUN_LEVELUP" ||
    model.phase === "PAUSE_MENU" ||
    model.phase === "VERSUS_MATCH";
  const isGameplay = model.run != null && gameplayPhase;

  if (menuPhase) {
    const mobileBar = true; // unified top bar layout

    // --- Top bar row 1: profile left, settings+friends right ---
    const mobileHeader = isMobileViewport();
    const menuTopOffset = resolveMenuSafeTopOffset(menuSafeAreaTop, mobileHeader);
    const topBarY = 16 + menuTopOffset;
    const iconBtnSize = mobileHeader ? 28 : 18;
    const pfpSize = mobileHeader ? 26 : 20;
    const row1CenterY = topBarY + pfpSize / 2;
    const topRightActionRects = resolveMenuHeaderTopRightActionRects(CANVAS_WIDTH, mobileHeader, menuTopOffset);

    // Settings icon
    const settingsIconRect = topRightActionRects.settings;
    const settingsImg = uiSettingsIcon ?? settingsIconImage;
    if (settingsImg != null) {
      const hPad = Math.round(settingsIconRect.w * 0.15);
      ctx.drawImage(settingsImg, settingsIconRect.x + hPad, settingsIconRect.y + hPad, settingsIconRect.w - hPad * 2, settingsIconRect.h - hPad * 2);
    } else {
      // Fallback: simple circle+dots gear
      const gearCx = settingsIconRect.x + settingsIconRect.w / 2;
      const gearCy = settingsIconRect.y + settingsIconRect.h / 2;
      const gearScale = iconBtnSize / 14;
      ctx.strokeStyle = "#7cc2a0";
      ctx.lineWidth = 1.2 * gearScale;
      ctx.beginPath();
      ctx.arc(gearCx, gearCy, 2.7 * gearScale, 0, Math.PI * 2);
      ctx.stroke();
      for (let a = 0; a < 6; a += 1) {
        const angle = (a * Math.PI) / 3;
        const ix = gearCx + Math.cos(angle) * 4.5 * gearScale;
        const iy = gearCy + Math.sin(angle) * 4.5 * gearScale;
        ctx.fillStyle = "#7cc2a0";
        ctx.fillRect(ix - 0.9 * gearScale, iy - 0.9 * gearScale, 1.8 * gearScale, 1.8 * gearScale);
      }
    }
    headerRects.settings = settingsIconRect;

    // Friends/contact icon
    const friendsRect = topRightActionRects.friends;
    const friendsIconX = friendsRect.x + 2;
    const contactImg = uiContactIcon ?? playerIconImage;
    if (contactImg != null) {
      const pPad = Math.round(friendsRect.w * 0.15);
      ctx.drawImage(contactImg, friendsRect.x + pPad, friendsRect.y + pPad, friendsRect.w - pPad * 2, friendsRect.h - pPad * 2);
    } else {
      const fCx = friendsRect.x + friendsRect.w / 2;
      const fCy = friendsRect.y + friendsRect.h / 2;
      const fScale = iconBtnSize / 14;
      ctx.fillStyle = "#4dff88";
      ctx.beginPath();
      ctx.arc(fCx, fCy - 2.2 * fScale, 2.3 * fScale, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(fCx, fCy + 3.4 * fScale, 4.1 * fScale, Math.PI, 0);
      ctx.fill();
    }
    if (model.friendsOnlineCount > 0) {
      const badgeX = friendsRect.x + friendsRect.w - 1;
      const badgeY = friendsRect.y + 2;
      ctx.fillStyle = "#4dff88";
      ctx.beginPath();
      ctx.arc(badgeX, badgeY + 4, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#0a1020";
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(model.friendsOnlineCount), badgeX, badgeY + 4);
    }
    headerRects.friends = friendsRect;

    // Profile portrait with vinyl record background
    const char = model.equippedCharacter;
    const pfpX = 14;
    const ringCx = pfpX + pfpSize / 2;
    const ringCy = topBarY + pfpSize / 2;

    if (mobileBar) {
      // Vinyl record as background behind portrait
      const hudTier: RankedTier = model.versusRankContext?.player?.tier ?? "Regular";
      const hudVinylImg = rankVinylImages[hudTier] ?? null;
      const vinylDrawSize = pfpSize + 12; // vinyl slightly larger than portrait
      if (hudVinylImg) {
        ctx.save();
        // Slow rotation
        const vinylAngle = (getNowMs() / 8000) * Math.PI * 2;
        ctx.translate(ringCx, ringCy);
        ctx.rotate(vinylAngle);
        ctx.drawImage(hudVinylImg, -vinylDrawSize / 2, -vinylDrawSize / 2, vinylDrawSize, vinylDrawSize);
        ctx.restore();
      }

      // Portrait drawn large but clipped to a tight circle matching the vinyl center
      const portraitScale = 1.7;
      const scaledPfp = pfpSize * portraitScale;
      const clipR = pfpSize / 2 + 5;
      loadCharacterImage(char.imageSmallPath);
      const pfpImg = characterImages.get(char.imageSmallPath);
      if (pfpImg != null) {
        const drawRect = resolvePortraitContainRect(
          pfpImg.naturalWidth || pfpImg.width || 1,
          pfpImg.naturalHeight || pfpImg.height || 1,
          { x: ringCx - scaledPfp / 2, y: ringCy - scaledPfp / 2, w: scaledPfp, h: scaledPfp },
          "center"
        );
        ctx.save();
        ctx.beginPath();
        ctx.arc(ringCx, ringCy, clipR, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(pfpImg, drawRect.x, drawRect.y, drawRect.w, drawRect.h);
        ctx.restore();
      }

      // XP progress arc as vinyl edge ring
      const ringR = vinylDrawSize / 2 + 1;
      const ringWidth = 3;
      // Background ring
      ctx.beginPath();
      ctx.arc(ringCx, ringCy, ringR, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(30,50,80,0.8)";
      ctx.lineWidth = ringWidth;
      ctx.stroke();
      // XP progress arc (starts at top, clockwise)
      if (model.level > 0 && model.xpProgress > 0) {
        const startAngle = -Math.PI / 2;
        const xpFrac = Math.min(1, model.xpProgress);
        const endAngle = startAngle + Math.PI * 2 * xpFrac;
        ctx.beginPath();
        ctx.arc(ringCx, ringCy, ringR, startAngle, endAngle);
        ctx.strokeStyle = "#ffd700";
        ctx.lineWidth = ringWidth;
        ctx.lineCap = "round";
        ctx.stroke();
        ctx.lineCap = "butt";
        // Glowing tip at end of progress arc
        const tipX = ringCx + Math.cos(endAngle) * ringR;
        const tipY = ringCy + Math.sin(endAngle) * ringR;
        const tipPulse = 0.5 + 0.5 * Math.sin(getNowMs() / 300);
        ctx.save();
        ctx.shadowColor = "#ffd700";
        ctx.shadowBlur = 6 + 6 * tipPulse;
        ctx.beginPath();
        ctx.arc(tipX, tipY, ringWidth * 0.7, 0, Math.PI * 2);
        ctx.fillStyle = "#fff8cc";
        ctx.fill();
        ctx.restore();
      }
    } else {
      drawCharacterPortrait(char.imageSmallPath, pfpX, topBarY, pfpSize, pfpSize);
      ctx.strokeStyle = "#5e82ba";
      ctx.lineWidth = 1;
      roundedRectPath(ctx, pfpX, topBarY, pfpSize, pfpSize, 4);
      ctx.stroke();
    }

    // Clickable profile pic rect
    const hitSize = pfpSize + 12;
    headerRects.headerProfile = {
      x: ringCx - hitSize / 2,
      y: ringCy - hitSize / 2,
      w: hitSize,
      h: hitSize
    };

    const rawName = (model.username ?? "PLAYER").toUpperCase();
    if (mobileBar) {
      // --- Unified title header: 2 rows stacked next to pfp (XP is now the ring) ---
      // Row A: level + username | Row B: wallet resources
      const infoX = pfpX + pfpSize + 14;
      const infoRightLimit = friendsIconX - 10;
      const infoMaxW = Math.max(30, infoRightLimit - infoX);
      const pfpCenterY = topBarY + pfpSize / 2;

      // Row A: Username + level (upper half of pfp)
      const isMobile = mobileHeader;
      const rowAY = pfpCenterY - (isMobile ? 10 : 9);
      const levelPrefix = model.level > 0 ? `${model.level}${prestigeStarSuffix(model.prestigeStars)}` : "";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      if (isMobile) {
        const nameLayout = resolveMobileMenuHeaderNameRowLayout({
          levelPrefix,
          username: rawName,
          maxWidth: infoMaxW,
          measureWidthAtPx: (fontPx, text) => {
            ctx.font = `bold ${fontPx}px ${GAME_FONT}`;
            return measureTextWidth(text);
          }
        });
        ctx.font = `bold ${nameLayout.fontPx}px ${GAME_FONT}`;
        ctx.save();
        ctx.translate(infoX, rowAY);
        if (nameLayout.scaleX < 1) ctx.scale(nameLayout.scaleX, 1);
        if (model.level > 0) {
          ctx.fillStyle = "#ffd700";
          ctx.fillText(levelPrefix, 0, 0);
        }
        ctx.fillStyle = "#c8d8f0";
        ctx.fillText(rawName, nameLayout.usernameOffsetX, 0);
        ctx.restore();
      } else {
        const nameLayout = resolveDesktopMenuHeaderNameRowLayout({
          levelPrefix,
          username: rawName,
          maxWidth: infoMaxW,
          measureWidthAtPx: (fontPx, text) => {
            ctx.font = `bold ${fontPx}px ${GAME_FONT}`;
            return measureTextWidth(text);
          }
        });
        ctx.font = `bold ${nameLayout.fontPx}px ${GAME_FONT}`;
        ctx.save();
        ctx.translate(infoX, rowAY);
        if (nameLayout.scaleX < 1) ctx.scale(nameLayout.scaleX, 1);
        if (model.level > 0) {
          ctx.fillStyle = "#ffd700";
          ctx.fillText(levelPrefix, 0, 0);
        }
        ctx.fillStyle = "#c8d8f0";
        ctx.fillText(rawName, nameLayout.usernameOffsetX, 0);
        ctx.restore();
      }

      // Row B: Coins + Gems + Stamina/Premium (lower half of pfp)
      const rowBY = pfpCenterY + (isMobile ? 4 : 5);
      const walletIconSize = isMobile ? 18 : 14;
      const walletCenterY = rowBY;
      const walletIconY = walletCenterY - walletIconSize / 2;
      const walletFontPx = isMobile ? 8 : 8;
      ctx.font = `bold ${walletFontPx}px ${GAME_FONT}`;
      const gemStr = compactCount(model.gems);
      const coinStr = String(model.coins);
      const staminaText = model.staminaPremiumActive ? "PREMIUM" : `${model.staminaCurrent}`;
      const coinTextW = measureTextWidth(coinStr);
      const gemTextW = measureTextWidth(gemStr);

      const walletFont = `bold ${walletFontPx}px ${GAME_FONT}`;
      const heartScale = 1.35;
      const heartSize = Math.round(walletIconSize * heartScale);
      const staminaTextW = measureTextWidth(staminaText);
      const resourceLayout = resolveTitleHeaderResourceRowLayout({
        startX: infoX,
        centerY: walletCenterY,
        mobile: isMobile,
        walletIconSize,
        heartSize,
        coinTextWidth: coinTextW,
        gemTextWidth: gemTextW,
        staminaTextWidth: staminaTextW
      });

      // Coin section with bounce on change
      const coinScale = valueBounceScale(state.coinBounceMs);
      if (coinScale !== 1) {
        ctx.save();
        ctx.translate(resourceLayout.coin.iconX + walletIconSize, walletCenterY);
        ctx.scale(coinScale, coinScale);
        ctx.translate(-(resourceLayout.coin.iconX + walletIconSize), -walletCenterY);
      }
      drawCoinIcon(resourceLayout.coin.iconX, walletIconY, walletIconSize);
      state.headerCoinIconRect = { x: resourceLayout.coin.iconX, y: walletIconY, w: walletIconSize, h: walletIconSize };
      ctx.font = walletFont;
      ctx.fillStyle = "#ffd700";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(coinStr, resourceLayout.coin.textX, walletCenterY);
      if (coinScale !== 1) ctx.restore();

      // Gem section with bounce on change
      const gemScale = valueBounceScale(state.gemBounceMs);
      if (gemScale !== 1) {
        ctx.save();
        ctx.translate(resourceLayout.gem.iconX + walletIconSize, walletCenterY);
        ctx.scale(gemScale, gemScale);
        ctx.translate(-(resourceLayout.gem.iconX + walletIconSize), -walletCenterY);
      }
      drawGemIcon(resourceLayout.gem.iconX, walletIconY, walletIconSize);
      state.headerGemIconRect = { x: resourceLayout.gem.iconX, y: walletIconY, w: walletIconSize, h: walletIconSize };
      ctx.font = walletFont;
      ctx.fillStyle = "#64d8ff";
      ctx.fillText(gemStr, resourceLayout.gem.textX, walletCenterY);
      if (gemScale !== 1) ctx.restore();
      if (isHeaderGemHotspotEnabledForPhase(model.phase)) {
        headerRects.coins = resourceLayout.coinHotspot;
        headerRects.gemShop = resourceLayout.gemHotspot;
      }

      // Heart life icon + count
      if (model.staminaPremiumActive) {
        const premImg = heartLifePremiumImage;
        if (premImg != null) {
          const glowPulse = 0.6 + 0.4 * Math.sin(getNowMs() / 400);
          ctx.save();
          ctx.shadowColor = `rgba(255, 215, 0, ${glowPulse})`;
          ctx.shadowBlur = 8 + glowPulse * 4;
          const heartY = walletCenterY - heartSize / 2;
          ctx.drawImage(premImg, resourceLayout.stamina.iconX, heartY, heartSize, heartSize);
          ctx.restore();
        }
        ctx.fillStyle = "#ffd700";
        ctx.font = walletFont;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(staminaText, resourceLayout.stamina.textX, walletCenterY);
      } else {
        const heartImg = heartLifeImage;
        const heartY = walletCenterY - heartSize / 2;
        if (heartImg != null) {
          ctx.save();
          if (model.staminaCurrent <= 0) {
            ctx.globalAlpha = 0.25;
            ctx.filter = "grayscale(1)";
          }
          ctx.drawImage(heartImg, resourceLayout.stamina.iconX, heartY, heartSize, heartSize);
          ctx.restore();
        }
        ctx.fillStyle = model.staminaCurrent <= 0 ? "#ff4444" : "#fff";
        ctx.font = walletFont;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(staminaText, resourceLayout.stamina.textX, walletCenterY);
      }
      // Tappable heart hotspot
      headerRects.staminaHeart = {
        x: resourceLayout.stamina.iconX - 4,
        y: walletCenterY - heartSize / 2 - 4,
        w: resourceLayout.stamina.right - resourceLayout.stamina.iconX + 8,
        h: heartSize + 8,
      };
    } else {
      // --- Desktop: original single-row layout ---
      const nameX = pfpX + pfpSize + 8;
      const nameRightLimit = friendsIconX - 90;
      const nameMaxW = Math.max(30, nameRightLimit - nameX - 6);
      const levelPrefix = model.level > 0 ? `${model.level}${prestigeStarSuffix(model.prestigeStars)} ` : "";
      const topBarName = `${levelPrefix}${rawName}`;
      let fontSize = 6;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.font = `bold ${fontSize}px ${GAME_FONT}`;
      while (fontSize > 4 && measureTextWidth(topBarName) > nameMaxW) {
        fontSize -= 0.5;
        ctx.font = `bold ${fontSize}px ${GAME_FONT}`;
      }
      const levelW = measureTextWidth(levelPrefix);
      const fullW = measureTextWidth(topBarName);
      const nameY = topBarY + pfpSize / 2;
      const scaleX = fullW > nameMaxW && fullW > 0 ? nameMaxW / fullW : 1;
      ctx.save();
      ctx.translate(nameX, nameY);
      if (scaleX < 1) ctx.scale(scaleX, 1);
      if (model.level > 0) {
        ctx.fillStyle = "#ffd700";
        ctx.fillText(levelPrefix, 0, 0);
      }
      ctx.fillStyle = "#c8d8f0";
      ctx.fillText(rawName, levelW, 0);
      ctx.restore();

      // XP bar under username with shimmer
      if (model.level > 0) {
        const barX = nameX;
        const barY = topBarY + pfpSize - 3;
        const barW = Math.max(18, Math.min(78, nameRightLimit - barX - 10));
        const barH = 3;
        const xpFillW = barW * Math.min(1, model.xpProgress);
        ctx.fillStyle = "rgba(30,50,80,0.8)";
        ctx.fillRect(barX, barY, barW, barH);
        ctx.fillStyle = "#ffd700";
        ctx.fillRect(barX, barY, xpFillW, barH);
        // Shimmer sweep across filled portion
        if (xpFillW > 4) {
          const xpShimT = (getNowMs() % 2000) / 2000;
          const shimX = barX + xpFillW * (xpShimT * 1.3 - 0.15);
          const shimW = xpFillW * 0.3;
          ctx.save();
          ctx.beginPath();
          ctx.rect(barX, barY, xpFillW, barH);
          ctx.clip();
          const shimGrad = getFrameLinearGradient(
            `header-xp-shimmer|${barX}|${barY}|${xpFillW.toFixed(3)}|${shimX.toFixed(3)}|${shimW.toFixed(3)}`,
            () => {
              const gradient = ctx.createLinearGradient(shimX, 0, shimX + shimW, 0);
              gradient.addColorStop(0, "rgba(255,255,255,0)");
              gradient.addColorStop(0.5, "rgba(255,255,255,0.35)");
              gradient.addColorStop(1, "rgba(255,255,255,0)");
              return gradient;
            }
          );
          ctx.fillStyle = shimGrad;
          ctx.fillRect(barX, barY, xpFillW, barH);
          ctx.restore();
        }
        ctx.strokeStyle = "#5e82ba";
        ctx.lineWidth = 0.5;
        ctx.strokeRect(barX, barY, barW, barH);
      }

      // Wallet: right-aligned before friends icon
      const walletIconSize = 16;
      const walletIconCenterY = row1CenterY;
      const walletIconY = walletIconCenterY - walletIconSize / 2;
      const gemStr = compactCount(model.gems);
      const coinStr = compactCount(model.coins);
      ctx.font = mFont("bold", 6);
      const gemTextW = measureTextWidth(gemStr);
      const coinTextW = measureTextWidth(coinStr);
      let rx = friendsIconX - 12;
      const gemRightEdge = rx;
      // Gem section with bounce on change
      const dGemScale = valueBounceScale(state.gemBounceMs);
      if (dGemScale !== 1) {
        ctx.save();
        ctx.translate(rx, walletIconCenterY);
        ctx.scale(dGemScale, dGemScale);
        ctx.translate(-rx, -walletIconCenterY);
      }
      ctx.fillStyle = "#64d8ff";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(gemStr, rx, walletIconCenterY);
      rx -= gemTextW + 2;
      drawGemIcon(rx - walletIconSize, walletIconY, walletIconSize);
      if (dGemScale !== 1) ctx.restore();
      const gemLeftEdge = rx - walletIconSize;
      rx -= walletIconSize + 8;
      const coinRightEdge = rx;
      // Coin section with bounce on change
      const dCoinScale = valueBounceScale(state.coinBounceMs);
      if (dCoinScale !== 1) {
        ctx.save();
        ctx.translate(rx, walletIconCenterY);
        ctx.scale(dCoinScale, dCoinScale);
        ctx.translate(-rx, -walletIconCenterY);
      }
      ctx.fillStyle = "#ffd700";
      ctx.fillText(coinStr, rx, walletIconCenterY);
      rx -= coinTextW + 2;
      drawCoinIcon(rx - walletIconSize, walletIconY, walletIconSize);
      if (dCoinScale !== 1) ctx.restore();
      const coinLeftEdge = rx - walletIconSize;
      if (isHeaderGemHotspotEnabledForPhase(model.phase)) {
        headerRects.coins = { x: coinLeftEdge - 2, y: walletIconY - 2, w: coinRightEdge - coinLeftEdge + 4, h: walletIconSize + 4 };
        headerRects.gemShop = { x: gemLeftEdge - 2, y: walletIconY - 2, w: gemRightEdge - gemLeftEdge + 4, h: walletIconSize + 4 };
      }

      // Heart life icon + count (right-to-left, left of coins)
      rx = coinLeftEdge - 8;
      const dHeartScale = 1.35;
      const dHeartSize = Math.round(walletIconSize * dHeartScale);
      const dHeartY = walletIconCenterY - dHeartSize / 2;
      if (model.staminaPremiumActive) {
        const dPremImg = heartLifePremiumImage;
        const rx2 = rx - dHeartSize;
        if (dPremImg != null) {
          const glowPulse = 0.6 + 0.4 * Math.sin(getNowMs() / 400);
          ctx.save();
          ctx.shadowColor = `rgba(255, 215, 0, ${glowPulse})`;
          ctx.shadowBlur = 8 + glowPulse * 4;
          ctx.drawImage(dPremImg, rx2, dHeartY, dHeartSize, dHeartSize);
          ctx.restore();
        }
        rx -= dHeartSize + 2;
        ctx.fillStyle = "#ffd700";
        ctx.font = mFont("bold", 6);
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const premW = ctx.measureText("PREMIUM").width;
        rx -= premW;
        ctx.fillText("PREMIUM", rx, walletIconCenterY);
        rx -= 2;
      } else {
        const countStr = `${model.staminaCurrent}`;
        ctx.fillStyle = model.staminaCurrent <= 0 ? "#ff4444" : "#fff";
        ctx.font = mFont("bold", 6);
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const countW = ctx.measureText(countStr).width;
        rx -= countW;
        ctx.fillText(countStr, rx, walletIconCenterY);
        rx -= 2;
        const dHeartImg = heartLifeImage;
        if (dHeartImg != null) {
          rx -= dHeartSize;
          ctx.save();
          if (model.staminaCurrent <= 0) {
            ctx.globalAlpha = 0.25;
            ctx.filter = "grayscale(1)";
          }
          ctx.drawImage(dHeartImg, rx, dHeartY, dHeartSize, dHeartSize);
          ctx.restore();
        }
        rx -= 2;
      }
      // Tappable heart hotspot (desktop)
      headerRects.staminaHeart = {
        x: rx - 4,
        y: dHeartY - 4,
        w: coinLeftEdge - rx + 4,
        h: dHeartSize + 8,
      };
    }

    // --- Title logo (only on title/map screen) ---
    if (model.phase === "TITLE") {
      const titleLogoTopBarOffsetY = 6;
      const titleLogoFallbackY = 46;
      const titleY = mobileBar ? topBarY + pfpSize + titleLogoTopBarOffsetY : titleLogoFallbackY;
      const titleLogoImg = titleLogo;
      const logoTimeSec = getNowSec();
      const musicBass = model.musicVisual?.bass ?? 0;
      const musicRms = model.musicVisual?.rms ?? 0;
      const hasMusicData = musicBass > 0.01 || musicRms > 0.01;
      const logoFloat = hasMusicData
        ? musicBass * 4
        : Math.sin(logoTimeSec * 1.2) * 3;
      const logoGlowPulse = hasMusicData
        ? 0.3 + 0.7 * musicBass
        : 0.5 + 0.5 * Math.sin(logoTimeSec * 2.0);
      const logoScale = resolveTitleLogoScale(logoTimeSec);

      if (titleLogoReady && titleLogoImg != null) {
        const naturalW = titleLogoImg.naturalWidth || 200;
        const naturalH = titleLogoImg.naturalHeight || 40;
        const logoW = 140;
        const logoH = Math.round(logoW * (naturalH / naturalW));
        const logoX = Math.round((CANVAS_WIDTH - logoW) / 2);
        const logoDrawY = titleY + logoFloat;
        ctx.save();
        if (logoScale !== 1) {
          const lCx = logoX + logoW / 2;
          const lCy = logoDrawY + logoH / 2;
          ctx.translate(lCx, lCy);
          ctx.scale(logoScale, logoScale);
          ctx.translate(-lCx, -lCy);
        }
        ctx.shadowColor = `rgba(180, 100, 255, ${(0.4 + 0.5 * logoGlowPulse).toFixed(2)})`;
        ctx.shadowBlur = 10 + 14 * logoGlowPulse;
        ctx.drawImage(titleLogoImg, logoX, logoDrawY, logoW, logoH);
        ctx.restore();
      } else {
        const hue = (getNowMs() / 9) % 360;
        ctx.font = `bold 10px ${GAME_FONT}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.save();
        if (logoScale !== 1) {
          const tCx = CANVAS_WIDTH / 2;
          const tCy = titleY + logoFloat;
          ctx.translate(tCx, tCy);
          ctx.scale(logoScale, logoScale);
          ctx.translate(-tCx, -tCy);
        }
        ctx.shadowColor = `rgba(180, 100, 255, ${(0.5 + 0.4 * logoGlowPulse).toFixed(2)})`;
        ctx.shadowBlur = 10 + 12 * logoGlowPulse;
        ctx.fillStyle = `hsl(${hue.toFixed(1)},98%,70%)`;
        ctx.fillText("BITBEAST BLOCK PARTY", CANVAS_WIDTH / 2, titleY + logoFloat);
        ctx.shadowBlur = 0;
        ctx.fillStyle = "#ffffff";
        ctx.fillText("BITBEAST BLOCK PARTY", CANVAS_WIDTH / 2, titleY + logoFloat);
        ctx.restore();
      }
    }
  }

  if (isGameplay) {
    // Gems hidden during gameplay — no wallet display needed
  }

  if (!isGameplay || model.phase === "RUN_GAMEOVER") {
    state.lastSeenGameplayLevel = null;
    state.levelBadgeWiggleStartMs = -1;
    return headerRects;
  }

  const run = model.run!;
  const level = Math.max(1, Math.min(20, run.level));
  const isClassicProg = soloModeUsesClassicProgression(model.soloMode);
  // Classic: fills up toward target. Remaining modes: drains down as goal is completed.
  const levelProgress = isClassicProg
    ? (model.levelTarget > 0 ? clamp01(1 - model.pointsToNextLevel / model.levelTarget) : 0)
    : (model.goalTarget != null && model.goalTarget > 0 ? clamp01(model.goalRemaining / model.goalTarget) : 0);
  const cashBalance = Math.max(0, Math.floor(run.cashBalance));

  // === Unified top bar HUD ===
  // Match doom bar dimensions: 26px tall elements
  const doomBarH = 26;
  const topBarY = 6;
  const topBarH = doomBarH;
  const nowMs = getNowMs();
  const hudBtnSize = doomBarH;
  const mobileGameplayHud = isMobileViewport();

  // --- Top-right gameplay control: pause in solo, settings in versus ---
  const topRightHudRect = resolveGameplayTopRightHudRect(hudBtnSize);
  if (resolveGameplayTopRightHudControl(model.phase) === "settings") {
    drawGearButton(ctx, topRightHudRect, hudBtnSize, uiSettingsIcon ?? settingsIconImage);
  } else {
    drawCirclePauseButton(topRightHudRect);
  }
  headerRects.settings = topRightHudRect;

  // --- Level bar (left corner, progress bar extends to board right edge) ---
  const doomSkullW = 26;
  const doomSkullGap = 4;
  const levelBoxX = 4; // flush in the corner
  const levelBoxW = doomSkullW;
  const levelBoxH = doomSkullW;
  const levelBoxY = topBarY + Math.floor((topBarH - levelBoxH) / 2);
  if (state.lastSeenGameplayLevel == null) {
    state.lastSeenGameplayLevel = level;
    state.levelBadgeWiggleStartMs = -1;
  } else if (level > state.lastSeenGameplayLevel) {
    state.lastSeenGameplayLevel = level;
    state.levelBadgeWiggleStartMs = nowMs;
  } else if (level < state.lastSeenGameplayLevel) {
    state.lastSeenGameplayLevel = level;
    state.levelBadgeWiggleStartMs = -1;
  }
  const wiggleElapsedMs = state.levelBadgeWiggleStartMs >= 0
    ? nowMs - state.levelBadgeWiggleStartMs
    : LEVEL_BADGE_WIGGLE_DURATION_MS;
  const wiggle = resolveLevelBadgeWiggle(wiggleElapsedMs);
  if (wiggle.done) {
    state.levelBadgeWiggleStartMs = -1;
  }
  const motionScale = reducedMotionPreferred ? LEVEL_BADGE_REDUCED_MOTION_SCALE : 1;
  const badgeRotation = wiggle.rotationRad * motionScale;
  const badgeScale = 1 + (wiggle.scale - 1) * motionScale;
  const levelBadgeTheme = resolveLevelBadgeTheme(level);

  // Draw level number box (matches doom skull box dimensions)
  ctx.save();
  const levelBadgeCenterX = levelBoxX + levelBoxW / 2;
  const levelBadgeCenterY = levelBoxY + levelBoxH / 2;
  ctx.translate(levelBadgeCenterX, levelBadgeCenterY);
  ctx.rotate(badgeRotation);
  ctx.scale(badgeScale, badgeScale);
  ctx.translate(-levelBadgeCenterX, -levelBadgeCenterY);
  roundedRectPath(ctx, levelBoxX, levelBoxY, levelBoxW, levelBoxH, 5);
  ctx.fillStyle = levelBadgeTheme.fill;
  ctx.fill();
  ctx.strokeStyle = levelBadgeTheme.stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = levelBadgeTheme.text;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (isMobileViewport()) {
    // Mobile: just the number, centered
    ctx.font = mFont("bold", 11);
    ctx.fillText(`${level}`, levelBoxX + levelBoxW / 2, levelBoxY + levelBoxH / 2);
  } else {
    ctx.font = mFont("bold", 4);
    ctx.fillText("LVL", levelBoxX + levelBoxW / 2, levelBoxY + levelBoxH / 2 - 6);
    ctx.font = mFont("bold", 9);
    ctx.fillText(`${level}`, levelBoxX + levelBoxW / 2, levelBoxY + levelBoxH / 2 + 5);
  }
  ctx.restore();

  // --- Progress bar (doom-bar style segmented blocks) ---
  const progressBarX = levelBoxX + doomSkullW + doomSkullGap;
  const progressBarW = Math.floor(CANVAS_WIDTH / 2) - progressBarX;
  const progressBarH = doomBarH;
  const progressBarY = levelBoxY;
  // Bar background
  roundedRectPath(ctx, progressBarX, progressBarY, progressBarW, progressBarH, 5);
  ctx.fillStyle = "rgb(20,28,50)";
  ctx.fill();
  roundedRectPath(ctx, progressBarX, progressBarY, progressBarW, progressBarH, 5);
  ctx.strokeStyle = "rgb(80,100,140)";
  ctx.lineWidth = 1.2;
  ctx.stroke();
  // Segmented blocks
  {
    const blockGap = 3;
    const innerX = progressBarX + 4;
    const innerY = progressBarY + 4;
    const innerH = progressBarH - 8;
    const innerRight = progressBarX + progressBarW - 4;
    const totalInner = innerRight - innerX;
    // Calculate block count and width so blocks fill the space exactly
    const approxBlockW = 10;
    const totalBlocks = Math.max(1, Math.round(totalInner / (approxBlockW + blockGap)));
    const blockW = (totalInner - blockGap * (totalBlocks - 1)) / totalBlocks;
    const filledBlocks = Math.round(totalBlocks * levelProgress);
    const fillColor = "rgb(50,205,100)";
    for (let b = 0; b < totalBlocks; b++) {
      const bx = innerX + b * (blockW + blockGap);
      if (b < filledBlocks) {
        ctx.fillStyle = fillColor;
      } else {
        ctx.fillStyle = "rgb(30,45,70)";
      }
      roundedRectPath(ctx, bx, innerY, blockW, innerH, 3);
      ctx.fill();
      ctx.strokeStyle = "rgba(10,18,30,0.7)";
      ctx.lineWidth = 0.8;
      roundedRectPath(ctx, bx, innerY, blockW, innerH, 3);
      ctx.stroke();
    }
  }
  // --- SCORE + CASH (to the right of the progress bar) ---
  const barCenterY = progressBarY + progressBarH / 2;
  const scoreLabel = `${Math.floor(model.displayScore)}`;
  const cashLabel = `${cashBalance}`;
  const topStatLayout = resolveGameplayTopStatLayout({
    progressBarRightX: progressBarX + progressBarW,
    pauseButtonLeftX: topRightHudRect.x,
    scoreWidth: measureTextWidthAtPx(
      scoreLabel,
      mobileGameplayHud ? GAMEPLAY_TOP_STATS_MOBILE_FONT_PX : GAMEPLAY_TOP_STATS_DESKTOP_FONT_PX
    ),
    cashWidth: measureTextWidthAtPx(
      cashLabel,
      mobileGameplayHud ? GAMEPLAY_TOP_STATS_MOBILE_FONT_PX : GAMEPLAY_TOP_STATS_DESKTOP_FONT_PX
    ),
    mobile: mobileGameplayHud,
    fallbackScoreWidth: mobileGameplayHud
      ? measureTextWidthAtPx(scoreLabel, GAMEPLAY_TOP_STATS_FALLBACK_FONT_PX)
      : undefined,
    fallbackCashWidth: mobileGameplayHud
      ? measureTextWidthAtPx(cashLabel, GAMEPLAY_TOP_STATS_FALLBACK_FONT_PX)
      : undefined
  });
  const iconSz = topStatLayout.iconSize;
  const topStatShadow = resolveGameplayTopStatShadowStyle();
  const cashCueText = model.cashPopupAmount > 0 ? `+$${model.cashPopupAmount}` : "";
  const hypeCueVisible = model.hypeText.length > 0 && model.hypeTimer > 0;
  const widestCueWidth = Math.max(
    cashCueText.length > 0
      ? measureTextWidthAtPx(cashCueText, GAMEPLAY_CUE_STACK_CASH_FONT_PX * GAMEPLAY_CUE_STACK_CASH_MAX_SCALE)
      : 0,
    hypeCueVisible
      ? measureTextWidthAtPx(model.hypeText, GAMEPLAY_CUE_STACK_HYPE_FONT_PX * GAMEPLAY_CUE_STACK_HYPE_MAX_SCALE)
      : 0
  );
  const onboardingBubbleBottomY =
    model.onboardingState?.active
    && model.onboardingState.section === "how-to-play"
    && model.onboardingStep != null
      ? resolveOnboardingRafsbyLayout({
          canvasWidth: CANVAS_WIDTH,
          message: model.onboardingStep.message,
          measureTextWidth: (text) => measureTextWidthAtPx(text, 5, "")
        }).bubbleBottomY
      : null;
  const cueStackLayout = resolveGameplayCueStackLayout({
    topStatLayout,
    topStatBottomY: barCenterY + iconSz / 2,
    cashGroupCenterX: topStatLayout.cashIconX + (topStatLayout.safeRightX - topStatLayout.cashIconX) / 2,
    widestCueWidth,
    onboardingBubbleBottomY
  });

  // SCORE: drawn star + value
  ctx.font = mFont("bold", topStatLayout.fontPx);
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.save();
  ctx.shadowColor = topStatShadow.color;
  ctx.shadowOffsetX = topStatShadow.offsetX;
  ctx.shadowOffsetY = topStatShadow.offsetY;
  ctx.shadowBlur = topStatShadow.blur;
  drawHudStarIcon(topStatLayout.scoreIconX, barCenterY - iconSz / 2, iconSz);
  ctx.fillStyle = "#ffd700";
  ctx.fillText(scoreLabel, topStatLayout.scoreTextX, barCenterY + 1);
  ctx.restore();

  // CASH: drawn $ circle + value
  ctx.font = mFont("bold", topStatLayout.fontPx);
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.save();
  ctx.shadowColor = topStatShadow.color;
  ctx.shadowOffsetX = topStatShadow.offsetX;
  ctx.shadowOffsetY = topStatShadow.offsetY;
  ctx.shadowBlur = topStatShadow.blur;
  drawHudCashIcon(topStatLayout.cashIconX, barCenterY - iconSz / 2, iconSz);
  ctx.fillStyle = "#46ff9f";
  ctx.fillText(cashLabel, topStatLayout.cashTextX, barCenterY + 1);
  ctx.restore();

  // --- Objective row (mirrors level row: number box on right + label bar on left) ---
  if (model.goalLabel != null && model.goalTarget != null) {
    const goalDone = model.goalRemaining <= 0 && model.goalTarget > 0;
    const accent = goalDone ? "#32cd64" : soloModeAccent(model.soloMode);
    const objLabel = model.goalLabel.toUpperCase();
    const remaining = Math.max(0, model.goalRemaining);
    const colorHuntPreviewLayout = resolveColorHuntGoalPreviewLayout(mobileGameplayHud);
    const objectiveSubheadingFontPx = resolveGameplayObjectiveSubheadingFontPx(mobileGameplayHud);
    const objectiveSubtextFontPx = resolveGameplayObjectiveSubtextFontPx(mobileGameplayHud);

    const objRowY = levelBoxY + levelBoxH + 4;
    const objRowH = resolveGameplayObjectiveRowHeight(mobileGameplayHud);
    const objectiveNumBoxW = resolveGameplayObjectiveNumberBoxWidth(mobileGameplayHud);
    const objectiveUpperLineY = objRowY + Math.round(objRowH * 0.34);
    const objectiveLowerLineY = objRowY + Math.round(objRowH * 0.72);
    const modeLabel = isClassicProg ? "LEVEL BONUS" : soloModeLabel(model.soloMode).toUpperCase();
    const modeLabelWidth = measureTextWidthAtPx(
      modeLabel,
      objectiveSubheadingFontPx
    );
    const colorHuntParts = objLabel.includes("\x01") && model.goalTargetColors.length > 0
      ? objLabel.split("\x01")
      : null;
    const lowerLineWidth = goalDone && model.activeObjectiveReward != null
      ? resolveGameplayObjectiveLowerLineWidth({
          kind: "reward",
          amountWidths: model.activeObjectiveReward.grants
            .filter((grant) => grant.type === "score" || grant.type === "cash")
            .map((grant) => measureTextWidthAtPx(`${grant.amount}`, objectiveSubtextFontPx)),
          iconSize: GAMEPLAY_OBJECTIVE_REWARD_ICON_SIZE,
          iconTextGap: GAMEPLAY_OBJECTIVE_REWARD_ICON_TEXT_GAP,
          grantGap: GAMEPLAY_OBJECTIVE_REWARD_GRANT_GAP
        })
      : colorHuntParts != null
        ? resolveGameplayObjectiveLowerLineWidth({
            kind: "color-hunt",
            prefixWidth: measureTextWidthAtPx(colorHuntParts[0] ?? "", objectiveSubtextFontPx),
            suffixWidth: measureTextWidthAtPx(colorHuntParts[1] ?? "", objectiveSubtextFontPx),
            swatchCount: model.goalTargetColors.length,
            swatchSize: colorHuntPreviewLayout.previewSize,
            swatchGap: colorHuntPreviewLayout.previewGap
          })
        : resolveGameplayObjectiveLowerLineWidth({
            kind: "text",
            textWidth: measureTextWidthAtPx(objLabel, objectiveSubtextFontPx)
          });
    const objectiveLayout = resolveGameplayObjectiveBlockLayout({
      leftX: levelBoxX,
      maxRightX: cueStackLayout?.laneLeftX ?? topRightHudRect.x - UI_EDGE_PAD,
      numBoxW: objectiveNumBoxW,
      gap: doomSkullGap,
      paddingLeft: GAMEPLAY_OBJECTIVE_SIDE_PAD,
      paddingRight: GAMEPLAY_OBJECTIVE_SIDE_PAD,
      topLineWidth: modeLabelWidth,
      bottomLineWidth: lowerLineWidth
    });

    // Number box on right (same size as level box)
    const numBoxW = objectiveNumBoxW;
    const numBoxX = objectiveLayout.numBoxX;
    const numBoxFill = goalDone ? "rgb(30,80,50)" : "rgb(20,28,50)";
    const numBoxStroke = goalDone ? "rgb(50,205,100)" : "rgb(80,100,140)";
    roundedRectPath(ctx, numBoxX, objRowY, numBoxW, objRowH, 5);
    ctx.fillStyle = numBoxFill;
    ctx.fill();
    roundedRectPath(ctx, numBoxX, objRowY, numBoxW, objRowH, 5);
    ctx.strokeStyle = numBoxStroke;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (goalDone && checkCompleteImage != null) {
      const iconSz = mobileGameplayHud ? 14 : 16;
      ctx.drawImage(checkCompleteImage, numBoxX + (numBoxW - iconSz) / 2, objRowY + (objRowH - iconSz) / 2, iconSz, iconSz);
    } else if (model.activeObjective?.kind === "SHAPE_MATCH" && model.activeObjective.targetShape) {
      // Draw the target shape
      const ps = mobileGameplayHud ? 4 : 5;
      const shapeMap: Record<string, number[][]> = {
        L: [[1,0],[1,0],[1,1]],
        T: [[1,1,1],[0,1,0]],
        SQUARE: [[1,1],[1,1]],
      };
      const shape = shapeMap[model.activeObjective.targetShape] ?? shapeMap.SQUARE;
      const shapeW = shape[0].length * ps;
      const shapeH = shape.length * ps;
      const sx = numBoxX + (numBoxW - shapeW) / 2;
      const sy = objRowY + (objRowH - shapeH) / 2;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[r].length; c++) {
          if (shape[r][c]) {
            ctx.fillStyle = "#8ce6ff";
            ctx.fillRect(sx + c * ps, sy + r * ps, ps - 0.5, ps - 0.5);
          }
        }
      }
    } else {
      const numStr = `${remaining}`;
      const numFontSize = numStr.length >= 3
        ? (mobileGameplayHud ? 7 : 8)
        : (mobileGameplayHud ? 9 : 10);
      ctx.font = mFont("bold", numFontSize);
      ctx.fillStyle = "#fff";
      ctx.fillText(numStr, numBoxX + numBoxW / 2, objRowY + objRowH / 2 + 1);
    }

    // Label bar on left (fills space between left edge and number box)
    const labelBarX = levelBoxX;
    const labelBarW = objectiveLayout.labelBarW;
    const labelBarFill = goalDone ? "rgb(30,80,50)" : "rgb(20,28,50)";
    const labelBarStroke = goalDone ? "rgb(50,205,100)" : "rgb(80,100,140)";
    roundedRectPath(ctx, labelBarX, objRowY, labelBarW, objRowH, 5);
    ctx.fillStyle = labelBarFill;
    ctx.fill();
    roundedRectPath(ctx, labelBarX, objRowY, labelBarW, objRowH, 5);
    ctx.strokeStyle = labelBarStroke;
    ctx.lineWidth = 1;
    ctx.stroke();

    const labelClipW = Math.max(0, labelBarW - 2);
    ctx.save();
    ctx.beginPath();
    ctx.rect(labelBarX + 1, objRowY + 1, labelClipW, Math.max(0, objRowH - 2));
    ctx.clip();

    // Small label on top, objective/reward below (like LVL + number)
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = pxFont(objectiveSubheadingFontPx, "bold");
    ctx.fillStyle = "#bcd0f0";
    ctx.fillText(modeLabel, labelBarX + GAMEPLAY_OBJECTIVE_SIDE_PAD, objectiveUpperLineY);

    if (goalDone && model.activeObjectiveReward != null) {
      // Draw reward icons: star+score, cash — centered vertically
      const rewardY = objectiveLowerLineY;
      const iconSz = GAMEPLAY_OBJECTIVE_REWARD_ICON_SIZE;
      let rx = labelBarX + GAMEPLAY_OBJECTIVE_SIDE_PAD;
      for (const grant of model.activeObjectiveReward.grants) {
        if (grant.type === "score") {
          drawHudStarIcon(rx, rewardY - iconSz / 2, iconSz);
          rx += iconSz + GAMEPLAY_OBJECTIVE_REWARD_ICON_TEXT_GAP;
          ctx.font = pxFont(objectiveSubtextFontPx, "bold");
          ctx.fillStyle = "#ffd700";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(`${grant.amount}`, rx, rewardY);
          rx += measureTextWidth(`${grant.amount}`) + GAMEPLAY_OBJECTIVE_REWARD_GRANT_GAP;
        } else if (grant.type === "cash") {
          drawHudCashIcon(rx, rewardY - iconSz / 2, iconSz);
          rx += iconSz + GAMEPLAY_OBJECTIVE_REWARD_ICON_TEXT_GAP;
          ctx.font = pxFont(objectiveSubtextFontPx, "bold");
          ctx.fillStyle = "#46ff9f";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(`${grant.amount}`, rx, rewardY);
          rx += measureTextWidth(`${grant.amount}`) + GAMEPLAY_OBJECTIVE_REWARD_GRANT_GAP;
        }
      }
    } else {
      ctx.font = pxFont(objectiveSubtextFontPx, "bold");
      ctx.fillStyle = accent;
      // Color-hunt: draw inline miniature gameplay tiles where \x01 marker appears.
      if (colorHuntParts != null) {
        const textY = objectiveLowerLineY;
        let tx = labelBarX + GAMEPLAY_OBJECTIVE_SIDE_PAD;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        // "CLEAR "
        ctx.fillText(colorHuntParts[0] ?? "", tx, textY);
        tx += measureTextWidth(colorHuntParts[0] ?? "");
        const previewSize = colorHuntPreviewLayout.previewSize;
        const previewGap = colorHuntPreviewLayout.previewGap;
        for (let i = 0; i < model.goalTargetColors.length; i++) {
          const previewTile: TileData = {
            color: model.goalTargetColors[i] ?? 0,
            special: "NONE",
            lockHp: 0
          };
          drawTileSprite(previewTile, tx, textY - previewSize / 2, previewSize);
          tx += previewSize + previewGap;
        }
        // " BLOCKS"
        ctx.fillStyle = accent;
        ctx.fillText(colorHuntParts[1] ?? "", tx, textY);
      } else {
        ctx.fillText(objLabel, labelBarX + GAMEPLAY_OBJECTIVE_SIDE_PAD, objectiveLowerLineY);
      }
    }

    ctx.restore();
  }

  drawGameplayCueStack(model, cueStackLayout, feedbackMode);

  if (isMobileViewport() && model.phase === "VERSUS_MATCH") {
    return headerRects;
  }

  // --- Combo badge (positioned just above the board) ---
  const comboCount = Math.max(0, Math.floor(model.comboCount ?? 0));
  if (comboCount > 0) {
    const comboWindowDurationSec = Math.max(0.001, model.comboWindowDurationSec ?? 0.5);
    const comboWindowRemainingSec = Math.max(0, model.comboWindowRemainingSec ?? comboWindowDurationSec);
    const comboFill = clamp01(comboWindowRemainingSec / comboWindowDurationSec);
    drawComboBadge(comboCount, comboFill);
  }

  return headerRects;
}
